/**
 * CetusEd Admin Console — District Impersonation
 *
 * ⛔ COMPLIANCE (Rule 2: Fail Closed): Impersonation is a sensitive operation
 *   that MUST be explicitly audit-logged. Every impersonation session records:
 *   - The support staff member's identity
 *   - The target district and user
 *   - The reason for impersonation
 *   - Session start and end timestamps
 *
 *   FERPA §1232g(b)(4)(B) — Vendor support staff may only access PII when
 *   necessary for support. Impersonation is restricted to users with the
 *   'admin:impersonate:*' capability and requires a written reason.
 *
 *   The impersonation token expires after 15 minutes and grants the same
 *   RBAC as the target user, with all actions logged under the support
 *   staff's identity AND the target user's identity.
 */

import React, { useState, useCallback } from 'react';
import { useImpersonation } from '../../api/hooks';

export const Impersonate: React.FC = () => {
  const impersonate = useImpersonation();

  const [districtId, setDistrictId] = useState('');
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImpersonate = useCallback(async () => {
    if (!districtId.trim() || !userId.trim() || !reason.trim()) {
      setError('District ID, User ID, and Reason are required.');
      return;
    }

    setError(null);
    setResult(null);

    try {
      const response = await impersonate.mutateAsync({
        districtId: districtId.trim(),
        userId: userId.trim(),
        reason: reason.trim(),
      });
      setResult(`Impersonation token generated. Expires in ${response.expiresIn} seconds.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impersonation failed.');
    }
  }, [districtId, userId, reason, impersonate]);

  return (
    <div className="admin-page">
      <h1>District Impersonation</h1>

      <div className="impersonate-warning">
        <strong>Warning:</strong> Impersonation is audit-logged. Only use for legitimate
        support purposes. All actions taken during impersonation are recorded.
      </div>

      <div className="admin-form impersonate-form">
        <div className="form-field">
          <label htmlFor="imp-district">District ID</label>
          <input
            id="imp-district"
            type="text"
            value={districtId}
            onChange={(e) => setDistrictId(e.target.value)}
            required
            placeholder="e.g., acme-sd-123"
          />
        </div>

        <div className="form-field">
          <label htmlFor="imp-user">Target User ID</label>
          <input
            id="imp-user"
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
            placeholder="User ID to impersonate"
          />
        </div>

        <div className="form-field">
          <label htmlFor="imp-reason">Reason (required)</label>
          <textarea
            id="imp-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={3}
            maxLength={500}
            placeholder="Explain why impersonation is necessary..."
          />
        </div>

        {error && <p className="form-error" role="alert">{error}</p>}
        {result && <p className="form-success" role="status">{result}</p>}

        <div className="form-actions">
          <button
            className="admin-btn admin-btn--danger"
            onClick={handleImpersonate}
            disabled={impersonate.isPending}
          >
            {impersonate.isPending ? 'Generating...' : 'Start Impersonation'}
          </button>
        </div>
      </div>
    </div>
  );
};
